<template>
  <span class="text-base">
    <I18n :message="t['w.basket.summary.customer_discount.tooltip2']">
      <template #kontoraha>
        <strong>{{ usePriceHtml(userBalance) }}</strong>
      </template>
      <template #soodushind>{{ usePriceHtml(discountPriceSum) }}</template>
      <template #kontosoodus>
        <strong>{{ customerDiscountPercentage }}%</strong> ({{ usePriceHtml(customerDiscountAmount) }})
      </template>
      <template #sum>
        <strong>{{ usePriceHtml(priceSum) }}</strong>
      </template>
    </I18n>
  </span>
</template>

<script setup lang="ts">
import type { IBasketItem } from '~/components/order/BasketTypes'
import * as t from '~/messages'
import I18n from '~/components/i18n/I18n.vue'
import { usePriceHtml } from '~/composables/price'

const props = withDefaults(
  defineProps<{
    items: IBasketItem[]
    promoCodeDiscount: number | null
  }>(),
  {}
)

const user = useUserState()

const userBalance = computed(() => user.value?.balance ?? 0)

const customerDiscountPercentage = computed(() => Math.floor((1 - priceSum.value / discountPriceSum.value) * 100))

const customerDiscountAmount = computed(() => discountPriceSum.value - priceSum.value)

const discountPriceSum = computed(() => {
  if (!props.items || props.items.length < 1) {
    return 0
  }

  return props.items.reduce((accumulator, currentValue) => {
    return accumulator + currentValue.quantity * (currentValue.discountPrice ?? currentValue.regularPrice)
  }, 0)
})

const priceSum = computed(() => {
  if (!props.items || props.items.length < 1) {
    return 0
  }

  if (user.value) {
    return Math.max(itemSum.value - user.value.balance, customerDiscountPriceSum.value)
  }

  return itemSum.value
})

const itemSum = computed(() => {
  if (!props.items || props.items.length < 1) {
    return 0
  }

  return props.items.reduce((accumulator, currentValue) => {
    return accumulator + currentValue.quantity * (currentValue.discountPrice ?? currentValue.regularPrice)
  }, 0)
})

const customerDiscountPriceSum = computed(() => {
  if (!props.items || props.items.length < 1) {
    return 0
  }

  const sum = props.items.reduce((accumulator, currentValue) => {
    return accumulator + currentValue.quantity * (currentValue.customerPrice ?? currentValue.discountPrice ?? currentValue.regularPrice)
  }, 0)

  // FIXME: this part is still broken because it does calculations for whole cart, but we can have different item based discounts (one has promo, one has customer price)
  if (props.promoCodeDiscount && props.promoCodeDiscount > sum) {
    return 0
  }

  return Math.round(sum * 100) / 100
})
</script>
